/** Org create-project helpers. Mirrors worker slugify; GitHub is not part of create. */

import { ApiError } from "./api.ts";

/** Lowercase letters, digits, hyphens; 1–64 chars; start/end alphanumeric. */
export const PROJECT_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/** Same transform as worker `slugify` (`packages/mind-workbench/worker/db.ts`). */
export function slugifyProject(input: string): string {
  const s = input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return s || "item";
}

export function validateProjectSlug(slug: string): string | null {
  const s = slug.trim();
  if (!s) return "slug required";
  if (s.length > 64) return "slug must be 64 characters or fewer";
  if (s.includes("--")) return "slug cannot contain consecutive hyphens";
  if (!PROJECT_SLUG_RE.test(s)) {
    return "slug must be lowercase letters, numbers, and hyphens (start and end alphanumeric)";
  }
  return null;
}

export type OrgMemberRow = { user_id: string; role?: string };

/** Match POST /api/o/:org/projects: any organization member (owner or member). */
export function canCreateOrgProject(
  userId: string | null | undefined,
  members: OrgMemberRow[],
): boolean {
  if (!userId) return false;
  return members.some((m) => m.user_id === userId);
}

export function projectSlugTaken(slug: string, projects: { slug: string }[]): boolean {
  return projects.some((p) => p.slug === slug);
}

export function createProjectHomePath(orgSlug: string, projectSlug: string): string {
  return `/o/${orgSlug}/p/${projectSlug}/files`;
}

export function resolveCreateProjectFields(
  name: string,
  slugInput: string,
  projects: { slug: string }[] = [],
): { name: string; slug: string; error: string | null } {
  const trimmedName = name.trim();
  if (!trimmedName) return { name: "", slug: slugInput.trim(), error: "name required" };
  const slug = (slugInput.trim() || slugifyProject(trimmedName)).toLowerCase();
  const slugError = validateProjectSlug(slug);
  if (slugError) return { name: trimmedName, slug, error: slugError };
  if (projectSlugTaken(slug, projects)) {
    return { name: trimmedName, slug, error: "a project with this slug already exists in the organization" };
  }
  return { name: trimmedName, slug, error: null };
}

/** Body for POST /api/o/:org/projects — name + slug only. Do not send GitHub or hw_collab_loop. */
export function createProjectBody(name: string, slug: string): { name: string; slug: string } {
  return { name, slug };
}

export function formatCreateProjectError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Sign in required to create a project.";
    if (err.status === 404 && err.message === "org") {
      return "Organization not found or you are not a member.";
    }
    if (err.status === 403) {
      return `Forbidden (${err.message}). Only organization members can create a project.`;
    }
    if (/UNIQUE constraint failed: projects/i.test(err.message) || /already exists/i.test(err.message)) {
      return "A project with this slug already exists in the organization.";
    }
    return err.message || `create failed (${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "create project failed";
}

export function createProjectErrorField(error: string | null): "name" | "slug" | "form" | null {
  if (!error) return null;
  if (error === "name required") return "name";
  if (/slug|already exists/i.test(error)) return "slug";
  return "form";
}
